import { useMemo, useState } from "react";

import { TextboxControlWithDatePicker } from "@/Common/Components/Inputs/TextboxControlWithDatePicker";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { DateHelper } from "@/Common/Helpers/DateHelper";
import { DateTimeWithPrecision } from "@/Common/Models/DateTimeWithPrecision";
import { LifeStageDatePrecisionEnum } from "@/Common/Models/LifeStageDatePrecisionEnum";
import { LifeStageDateStatusEnum } from "@/Common/Models/LifeStageDateStatusEnum";
import { RiskFactorsSubCodes } from "@/Common/Resources/Lookups/Enums/RiskFactorLookupCodes";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { LinkButton } from "@/DesignSystem";
import { FlowsheetConstants } from "@/Flowsheets/FlowsheetConstants";
import { CareElementGridHelper } from "@/Flowsheets/Helpers/CareElementGridHelper";
import { CareElementFrequencyType } from "@/Flowsheets/Models/CareElementFrequencyType";
import { CareComponent } from "@/Flowsheets/Models/CareElements/CareComponent";
import { RiskFactorCareElement } from "@/Flowsheets/Models/CareElements/RiskFactorCareElement";
import { RiskFactorStatus } from "@/Flowsheets/Models/CareElements/RiskFactorStatus";
import { MedicalItemRiskFactorHelper } from "@/Patient/Helpers/MedicalItemRiskFactorHelper";
import { RiskFactorCategoryCode } from "@/Patient/Models/MedicalSummary/RiskFactorCode";
import CareComponentNotes from "./CareComponentParts/CareComponentNotes";
import { GridCell } from "./CareComponentParts/GridCell";
import { ICareComponentProps } from "./Types";

import styles from "./RiskFactorsCareComponent.module.scss";

interface RiskFactorsCareComponent extends CareComponent {
    CareElements: RiskFactorCareElement[];
}

const nonDisplayTobaccoRiskFactorCodes = new Set([
    RiskFactorsSubCodes.Tobacco.NonSmoker,
    RiskFactorsSubCodes.Tobacco.Occasional,
    RiskFactorsSubCodes.Tobacco.Pipe,
    RiskFactorsSubCodes.Tobacco.Shisha
]);

const nonDisplayAlcoholRiskFactorCodes = new Set([
    RiskFactorsSubCodes.Alcohol.Occasional,
    RiskFactorsSubCodes.Alcohol.NoAlcohol
]);

function useTobaccoRiskFactorDisplayResolver(): (riskFactorCodeId: string | null) => boolean {
    return useRiskFactorDisplayResolver(RiskFactorCategoryCode.Tobacco, nonDisplayTobaccoRiskFactorCodes);
}

function useAlcoholRiskFactorDisplayResolver(): (riskFactorCodeId: string | null) => boolean {
    return useRiskFactorDisplayResolver(RiskFactorCategoryCode.Alcohol, nonDisplayAlcoholRiskFactorCodes);
}

function useRiskFactorDisplayResolver(
    category: RiskFactorCategoryCode,
    nonDisplayCodes: Set<string>
): (riskFactorCodeId: string | null) => boolean {
    const idsThatDontDisplayRiskFactorName: Set<string> = useMemo(
        () => new Set(
            cacheStore.RiskFactorCodeCache[category]
                .filter(p => nonDisplayCodes.has(p.Code))
                .map(p => p.Id)
        ),
        [category, nonDisplayCodes]
    );

    function resolver(riskFactorCodeId: string | null): boolean {
        return !!riskFactorCodeId && !idsThatDontDisplayRiskFactorName.has(riskFactorCodeId);
    }

    return resolver;
}

function RiskFactorRow({ careElement }: Readonly<{ careElement: RiskFactorCareElement }>): JSX.Element {
    const { getText } = useTranslation();

    const tobaccoResolver = useTobaccoRiskFactorDisplayResolver();
    const alcoholResolver = useAlcoholRiskFactorDisplayResolver();

    let resolver = undefined;
    if (careElement.Name === FlowsheetConstants.RiskFactors.SmokingStatusCareElementName) {
        resolver = tobaccoResolver;
    }
    else if (careElement.Name === FlowsheetConstants.RiskFactors.AlcoholUseCareElementName) {
        resolver = alcoholResolver;
    }

    return (
        <>
            <div><label className={styles.rowLabel}>{getText(`Flowsheets_RiskFactorsCareComponentLabel_${careElement.Name}`)}</label></div>
            <div className={styles.radioButtonContainer}>
                <input
                    type="radio"
                    id={`${careElement.Name}-yes`}
                    checked={careElement.Status === RiskFactorStatus.Positive}
                    disabled
                />
                <label htmlFor={`${careElement.Name}-yes`}>
                    {getText("YesLabel")}
                </label>
            </div>
            <div className={styles.radioButtonContainer}>
                <input
                    type="radio"
                    id={`${careElement.Name}-no`}
                    checked={careElement.Status === RiskFactorStatus.Negative}
                    disabled
                />
                <label htmlFor={`${careElement.Name}-no`}>
                    {getText("NoLabel")}
                </label>
            </div>
            <div />
            <div>
                {careElement.ReportingPeriod?.StartDate && LuxonDateTimeParser.parse(careElement.ReportingPeriod?.StartDate).toFormat("yyyy-MM-dd")}
            </div>
            <div className={styles.consumptionContainer} title={careElement.RiskFactorCodeId && MedicalItemRiskFactorHelper.getRiskFactorLabelById(careElement.RiskFactorCodeId, careElement.Details)}>
                {resolver(careElement.RiskFactorCodeId) && MedicalItemRiskFactorHelper.getRiskFactorLabelById(careElement.RiskFactorCodeId, careElement.Details)}
            </div>
            <div>
                {careElement.ReportingPeriod?.EndDate && LuxonDateTimeParser.parse(careElement.ReportingPeriod?.EndDate).toFormat("yyyy-MM-dd")}
            </div>
        </>
    );
}

function ErectileFunctionStatus({ id, careElement, isEditable, encounterDate, onDateChange, onCheckBoxChange }: { id: string, careElement: RiskFactorCareElement, isEditable: boolean, encounterDate: Date, onDateChange: (dateTimeWithPrecision: DateTimeWithPrecision) => void, onCheckBoxChange: (value: RiskFactorStatus) => void }): JSX.Element {
    const { getText } = useTranslation();
    const [textValue, setTextvalue] = useState(careElement.ReportingPeriod?.StartDate ? DateHelper.formatDateLuxon(careElement.ReportingPeriod.StartDate, "yyyy-MM-dd") ?? "" : "");
    const autopopulatedDate = new DateTimeWithPrecision(DateHelper.formatDateLuxon(encounterDate, "yyyy-MM-dd"), "", LifeStageDatePrecisionEnum.Day);

    const isOverdue = (): boolean => {
        if (careElement.Frequency === CareElementFrequencyType.None || !encounterDate) {
            return false;
        }

        if (careElement.Status === RiskFactorStatus.Unknown || !careElement.ReportingPeriod?.StartDate)
            return true;

        const luxonEncounterDate = LuxonDateTimeParser.parse(encounterDate);

        switch (careElement.Frequency) {
            case CareElementFrequencyType.Annual:
                return LuxonDateTimeParser.parse(careElement.ReportingPeriod.StartDate).plus({ years: 1 }) < luxonEncounterDate;
            case CareElementFrequencyType.ThreeMonths:
                return LuxonDateTimeParser.parse(careElement.ReportingPeriod.StartDate).plus({ months: 3 }) < luxonEncounterDate;
            case CareElementFrequencyType.SixMonths:
                return LuxonDateTimeParser.parse(careElement.ReportingPeriod.StartDate).plus({ months: 6 }) < luxonEncounterDate;
            case CareElementFrequencyType.Biennial:
                return LuxonDateTimeParser.parse(careElement.ReportingPeriod.StartDate).plus({ years: 2 }) < luxonEncounterDate;
            default:
                return false;
        }
    };

    const showDatePicker = careElement.Status !== RiskFactorStatus.Unknown && isEditable;

    return (
        <>
            <div className={showDatePicker ? styles.erectileFunctionRow : ""}>
                <label className={styles.rowLabel}>{getText("Flowsheets_RiskFactorsCareComponentLabel_ErectileFunction")}</label>
            </div>
            <div className={`${styles.radioButtonContainer} ${showDatePicker ? styles.erectileFunctionRow : ""}`}>
                <input
                    type="radio"
                    id={`erectile-function-abnormal-${id}`}
                    checked={careElement.Status === RiskFactorStatus.Normal}
                    disabled={!isEditable}
                    onClick={() => {
                        if (careElement.Status === RiskFactorStatus.Unknown && !careElement.ReportingPeriod?.StartDate) {
                            setTextvalue(autopopulatedDate.text);
                            onDateChange(autopopulatedDate);
                        }
                        onCheckBoxChange(careElement.Status === RiskFactorStatus.Normal ? RiskFactorStatus.Unknown : RiskFactorStatus.Normal);
                    }}
                />
                <label htmlFor="erectile-function-abnormal">
                    {getText("NormalLabel")}
                </label>
            </div>
            <div className={`${styles.radioButtonContainer} ${showDatePicker ? styles.erectileFunctionRow : ""}`}>
                <input
                    type="radio"
                    id={`erectile-function-normal-${id}`}
                    checked={careElement.Status === RiskFactorStatus.Abnormal}
                    disabled={!isEditable}
                    onClick={() => {
                        if (careElement.Status === RiskFactorStatus.Unknown && !careElement.ReportingPeriod?.StartDate) {
                            setTextvalue(autopopulatedDate.text);
                            onDateChange(autopopulatedDate);
                        }
                        onCheckBoxChange(careElement.Status === RiskFactorStatus.Abnormal ? RiskFactorStatus.Unknown : RiskFactorStatus.Abnormal);
                    }}
                />
                <label htmlFor="erectile-function-normal">
                    {getText("AbnormalLabel")}
                </label>
            </div>
            <div className={`${styles.frequencyContainer} ${showDatePicker ? styles.erectileFunctionRow : ""}`}>
                <GridCell
                    id="riskFactors-erectile-function"
                    value={CareElementGridHelper.handleFrequencyLabel(careElement.Frequency, getText)}
                    showAlert={isOverdue()}
                    alertVariant="warning"
                    alertTooltipText={getText("Flowsheets_OutsideFrequencyTooltipLabel")}
                />
            </div>
            <div>
                {careElement.Status !== RiskFactorStatus.Unknown && (
                    !isEditable
                        ? careElement.ReportingPeriod?.StartDate && LuxonDateTimeParser.parse(careElement.ReportingPeriod.StartDate).toFormat("yyyy-MM-dd")
                        : (
                            <>
                                <TextboxControlWithDatePicker
                                    id={`riskfactors-erectile-function-${id}`}
                                    expectedPrecisionMinimum={LifeStageDatePrecisionEnum.Day}
                                    textValue={textValue}
                                    dateValue={careElement.ReportingPeriod?.StartDate}
                                    onChange={datePrecision => {
                                        setTextvalue(datePrecision.text);
                                        onDateChange(datePrecision);
                                    }}
                                    maxDate={encounterDate}
                                />
                                <LinkButton
                                    onClick={() => {
                                        const consultationDateWithPrecision = DateTimeWithPrecision.getDate(encounterDate);
                                        setTextvalue(consultationDateWithPrecision.text);
                                        onDateChange(consultationDateWithPrecision);
                                    }}
                                    underline={false}
                                    label={getText("TodayLabel")}
                                />
                            </>
                        )
                )}
            </div>
            <div />
            <div />
        </>
    );
}

function RiskFactorCareElementComponentFactory({ state, careElement, isEditable, encounterDate, onDateChange, onCheckboxChange }: { state: CareComponent, careElement: RiskFactorCareElement, isEditable: boolean, encounterDate: Date, onDateChange: (dateTimeWithPrecision: DateTimeWithPrecision) => void, onCheckboxChange: (value: RiskFactorStatus) => void }): JSX.Element {
    switch (careElement.Name) {
        case FlowsheetConstants.RiskFactors.SmokingStatusCareElementName:
        case FlowsheetConstants.RiskFactors.AlcoholUseCareElementName:
            return <RiskFactorRow careElement={careElement} />;
        case FlowsheetConstants.RiskFactors.ErectileFunctionCareElementName:
            return (
                <ErectileFunctionStatus
                    id={state.Id}
                    careElement={careElement}
                    isEditable={isEditable}
                    encounterDate={encounterDate}
                    onDateChange={onDateChange}
                    onCheckBoxChange={onCheckboxChange}
                />
            );
        default:
            return <></>;
    }
}

function RiskFactorsCareComponent({ isEditable = false, state, mutateState, encounterDate, onCareComponentValidityChange }: ICareComponentProps<RiskFactorsCareComponent>): JSX.Element {
    const { getText } = useTranslation();

    const handleDateChange = (index: number, datePrecision: DateTimeWithPrecision): void => {
        if (datePrecision == null
            || (datePrecision.status === LifeStageDateStatusEnum.ValidWithExtraChar
                && IsNullOrWhiteSpace(datePrecision.text))
        ) {
            mutateState(s => s.CareElements[index].ReportingPeriod = null);
            onCareComponentValidityChange("erectile-function-date", true);
            return;
        }

        if (datePrecision.status !== LifeStageDateStatusEnum.Valid && !datePrecision.isDayPrecision) {
            mutateState(s => s.CareElements[index].ReportingPeriod = null);
            onCareComponentValidityChange("erectile-function-date", false);
        }
        else {
            mutateState(s => s.CareElements[index].ReportingPeriod = { StartDate: datePrecision.value, EndDate: null });
            onCareComponentValidityChange("erectile-function-date", LuxonDateTimeParser.parse(datePrecision.value).diff(LuxonDateTimeParser.parse(encounterDate).endOf("day"), "days").days < 0);
        }
    };

    return (
        <div>
            <div className={styles.riskFactorsContainer}>
                <div className={`${styles.headerLabel}  ${styles.grid4}`}>
                    {getText("FrequencyLabel")}
                </div>
                <div className={`${styles.headerLabel}`}>
                    {getText("Flowsheets_RiskFactorsCareComponentLabel_DateReported")}
                </div>
                <div className={styles.headerLabel}>
                    {getText("ConsumptionLabel")}
                </div>
                <div className={styles.headerLabel}>
                    {getText("Flowsheets_RiskFactorsCareComponentLabel_DateReported")}
                </div>
                {state.CareElements.map((careElement, index) => (
                    <RiskFactorCareElementComponentFactory
                        key={`${careElement.Name}-${state.Id}`}
                        state={state}
                        careElement={careElement}
                        isEditable={isEditable}
                        encounterDate={encounterDate}
                        onDateChange={dateWithPrecision => handleDateChange(index, dateWithPrecision)}
                        onCheckboxChange={(value: RiskFactorStatus) => mutateState(s => {
                            s.CareElements[index].Status = value;
                            if (value === RiskFactorStatus.Unknown) {
                                s.CareElements[index].ReportingPeriod = null;
                            }
                            s.CareElements = [...s.CareElements];
                        })}
                    />
                ))}
            </div>
            <CareComponentNotes
                id={`riskFactorsNotes-${state.Id}`}
                dataCy="RiskFactorsNotes"
                value={state.Note}
                isEditable={isEditable}
                onTextChange={newValue => mutateState(s => s.Note = newValue)}
            />
        </div>
    );
}

export { RiskFactorsCareComponent };
